export const NO_CHANGE = Symbol('NO_CHANGE');

const etagByUrl = new Map<string, string>();
const abortControllerByUrl = new Map<string, AbortController>();
const inflightPromiseByUrl = new Map<string, Promise<unknown>>();

export type FetchJsonOptions = {
    signal?: AbortSignal;
    headers?: HeadersInit;
    timeoutMs?: number;
};

export async function fetchJsonWithETag<T = unknown>(
    absoluteOrRelativeUrl: string,
    options: FetchJsonOptions = {},
): Promise<T | typeof NO_CHANGE> {
    const existingPromise = inflightPromiseByUrl.get(absoluteOrRelativeUrl);
    if (existingPromise) {
    
        options.signal?.addEventListener(
            'abort',
            () => abortControllerByUrl.get(absoluteOrRelativeUrl)?.abort(),
            { once: true },
        );
        return existingPromise as Promise<T | typeof NO_CHANGE>;
    }

    // Abort any previous fetch for this URL
    abortControllerByUrl.get(absoluteOrRelativeUrl)?.abort();
    const controller = new AbortController();
    abortControllerByUrl.set(absoluteOrRelativeUrl, controller);

    const combinedHeaders: Record<string, string> = {
        Accept: 'application/json',
        ...(options.headers as Record<string, string> | undefined),
    };

    const previousEtag = etagByUrl.get(absoluteOrRelativeUrl);
    if (previousEtag) combinedHeaders['If-None-Match'] = previousEtag;

    const effectiveSignal = options.signal ?? controller.signal;
    const timeoutId =
        options.timeoutMs != null ? setTimeout(() => controller.abort(), options.timeoutMs) : null;

    const promise = (async () => {
        try {
            const response = await fetch(absoluteOrRelativeUrl, {
                method: 'GET',
                cache: 'no-store',
                headers: combinedHeaders,
                signal: effectiveSignal,
            });

            if (response.status === 304) return NO_CHANGE;

            if (!response.ok) {
                const text = await response.text().catch(() => '');
                throw new Error(
                    `GET ${absoluteOrRelativeUrl} failed: ${response.status} ${response.statusText} ${text.slice(0, 300)}`,
                );
            }

            const etagHeader = response.headers.get('ETag') ?? undefined;
            if (etagHeader) etagByUrl.set(absoluteOrRelativeUrl, etagHeader);

            return (await response.json()) as T;
        } finally {
            if (timeoutId) clearTimeout(timeoutId);
            if (abortControllerByUrl.get(absoluteOrRelativeUrl) === controller) {
                abortControllerByUrl.delete(absoluteOrRelativeUrl);
            }
            inflightPromiseByUrl.delete(absoluteOrRelativeUrl);
        }
    })();

    inflightPromiseByUrl.set(absoluteOrRelativeUrl, promise);
    return promise;
}

export function clearJsonEtag(absoluteOrRelativeUrl: string): void {
    etagByUrl.delete(absoluteOrRelativeUrl);
}
